import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { goBackOrHome } from '../../lib/navigation';
import { ArrowLeft, Users, Star, Clock, ArrowRightLeft, Calendar, AlertCircle } from 'lucide-react';
import { useUserStore } from '../../lib/userStore';
import { useLeagueStore } from '../../lib/leagueStore';
import { useLanguageStore } from '../../lib/languageStore';
import { useThemeStore } from '../../lib/themeStore';
import { translate } from '../../lib/translations';
import { DraftPick } from '../../lib/types/trade';
import toast from 'react-hot-toast';
import TradePlayersTable from './TradePlayersTable';
import TradeGoaliesTable from './TradeGoaliesTable';
import TradeDraftPicksTable from './TradeDraftPicksTable';
import TradePlacementModal, { PlayerPlacementItem } from './TradePlacementModal';
import { TeamLogo } from '../TeamLogo';
import {
  getUserByLeaguSaufUserConnecter,
  getMyteamPlayer,
  getTeamIdByTeamName,
  proposeTradeWithNotification,
  getMyTeamGoalies, 
  updateTradesId,
  sendCompleteTradeNotification,
  getUserDraftPicks,
  getTeamSalaryCapInfo,
  createTradeAuction,
  saveTradePreferences,
} from '../../lib/supabase';
import { supabase } from '../../lib/supabase';
import { usePageTracking } from '../../lib/hooks/usePageTracking';
import { useLeagueMode } from '../../lib/hooks/useLeagueMode';
import { ModeAccessMessage } from '../shared/ModeAccessMessage';
import { getLeagueSettings } from '../../lib/api/leagueSettings';

// PlayerCard component to display player information consistently
const PlayerCard = ({ 
  player, 
  onAction, 
  actionLabel, 
  language, 
  isTradeOffer = false, 
  theme,
  isSalaryRetainEnabled = false,
  retentionPct = 0,
  onRetentionChange,
  retentionCount = 0,
  maxRetentionSpots = 3,
  isUserPlayer = true
}: any) => (
  <div className={`card mb-2 border-0 shadow-sm hover-lift ${isTradeOffer ? 'border-primary border-2' : ''}`} 
       style={{ transition: 'all 0.3s ease' }}>
    <div className="card-body p-2 p-md-3">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-2 gap-md-3">
        <div className="flex-grow-1 w-100">
          <div className="d-flex align-items-center gap-2 mb-2">
            <div>
              <h6 className={`mb-0 fw-bold small d-md-none ${theme === 'dark' ? 'text-white' : 'text-primary'}`}>{player.skaterfullname || player.goaliefullname}</h6>
              <h6 className={`mb-0 fw-bold d-none d-md-block ${theme === 'dark' ? 'text-white' : 'text-primary'}`}>{player.skaterfullname || player.goaliefullname}</h6>
              {player.teamabbrevs && (
                <div className="text-muted small">{player.positioncode} • {player.teamabbrevs}</div>
              )}
            </div>
            <span className={`badge rounded-pill ${player.isnhl ? 'bg-success' : 'bg-warning'} ${player.is_scratch ? 'bg-danger' : ''}`}>
              <span className="d-none d-md-inline">
                {player.is_scratch ? translate('scratch.player', language) : (player.isnhl ? translate('main.club', language) : translate('reserves', language))}
              </span>
              <span className="d-md-none">
                {player.is_scratch ? 'S' : (player.isnhl ? 'NHL' : 'AHL')}
              </span>
            </span>
          </div>
          
          <div className="row g-1 g-md-2 mb-2">
            <div className="col-6">
              <small className="text-muted d-block">
                <strong className="d-none d-md-inline">{translate('salary', language)}:</strong>
                <strong className="d-md-none">$:</strong>
                {(() => {
                  const hasRetention = (player as any).salary_retention_received_pct && (player as any).salary_retention_received_pct > 0;
                  const retainedAmount = (player as any).salary_retention_received_amount || 0;
                  const effectiveSalary = hasRetention ? player.salary - retainedAmount : player.salary;
                  
                  return (
                    <>
                      <span className="d-none d-md-inline">
                        {hasRetention ? (
                          <>
                            <span className="text-success fw-bold">
                              {new Intl.NumberFormat('en-US', {
                                style: 'currency',
                                currency: 'USD',
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0,
                              }).format(effectiveSalary)}
                            </span>
                            <br />
                            <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                              {new Intl.NumberFormat('en-US', {
                                style: 'currency',
                                currency: 'USD',
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0,
                              }).format(player.salary)}
                              {' '}(-{(player as any).salary_retention_received_pct}%)
                            </span>
                          </>
                        ) : (
                          new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: 'USD',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          }).format(player.salary)
                        )}
                      </span>
                      <span className="d-md-none">
                        {hasRetention ? (
                          <>
                            <span className="text-success fw-bold">
                              {new Intl.NumberFormat('en-US', {
                                style: 'currency',
                                currency: 'USD',
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0,
                                notation: 'compact'
                              }).format(effectiveSalary)}
                            </span>
                            <br />
                            <span className="text-muted" style={{ fontSize: '0.7rem' }}>
                              {new Intl.NumberFormat('en-US', {
                                style: 'currency',
                                currency: 'USD',
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0,
                                notation: 'compact'
                              }).format(player.salary)}
                              {' '}(-{(player as any).salary_retention_received_pct}%)
                            </span>
                          </>
                        ) : (
                          new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: 'USD',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                            notation: 'compact'
                          }).format(player.salary)
                        )}
                      </span>
                    </>
                  );
                })()}
              </small>
            </div>
          </div>
          
          {player.goaliefullname ? (
            <div className="row g-1 g-md-2 small">
              <div className="col-4">
                <div className="text-center p-1 p-md-2 bg-light rounded">
                  <div className={`fw-bold small ${theme === 'dark' ? 'text-white' : 'text-primary'}`}>{player.wins}</div>
                  <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                    <span className="d-none d-md-inline">{translate('wins', language)}</span>
                    <span className="d-md-none">V</span>
                  </div>
                </div>
              </div>
              <div className="col-4">
                <div className="text-center p-1 p-md-2 bg-light rounded">
                  <div className={`fw-bold small ${theme === 'dark' ? 'text-white' : 'text-primary'}`}>{player.savepct.toFixed(3)}</div>
                  <div className="text-muted" style={{ fontSize: '0.7rem' }}>SV%</div>
                </div>
              </div>
              <div className="col-4">
                <div className="text-center p-1 p-md-2 bg-light rounded">
                  <div className={`fw-bold small ${theme === 'dark' ? 'text-white' : 'text-primary'}`}>{player.shutouts}</div>
                  <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                    <span className="d-none d-md-inline">{translate('shutouts', language)}</span>
                    <span className="d-md-none">SO</span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="row g-1 g-md-2 small">
              <div className="col-4">
                <div className="text-center p-1 p-md-2 bg-light rounded">
                  <div className={`fw-bold small ${theme === 'dark' ? 'text-white' : 'text-primary'}`}>{player.goals}</div>
                  <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                    <span className="d-none d-md-inline">{translate('goals', language)}</span>
                    <span className="d-md-none">G</span>
                  </div>
                </div>
              </div>
              <div className="col-4">
                <div className="text-center p-1 p-md-2 bg-light rounded">
                  <div className={`fw-bold small ${theme === 'dark' ? 'text-white' : 'text-primary'}`}>{player.assists}</div>
                  <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                    <span className="d-none d-md-inline">{translate('assists', language)}</span>
                    <span className="d-md-none">A</span>
                  </div>
                </div>
              </div>
              <div className="col-4">
                <div className="text-center p-1 p-md-2 bg-light rounded">
                  <div className={`fw-bold small ${theme === 'dark' ? 'text-white' : 'text-primary'}`}>{player.points}</div>
                  <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                    <span className="d-none d-md-inline">{translate('points', language)}</span>
                    <span className="d-md-none">P</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Salary Retention Input - Only show if enabled and in trade offer */}
          {isSalaryRetainEnabled && isTradeOffer && onRetentionChange && (() => {
            const hasExistingRetention = (player as any).salary_retention_received_pct && (player as any).salary_retention_received_pct > 0;
            const retentionLimitReached = isUserPlayer && retentionCount >= maxRetentionSpots && retentionPct === 0;
            const isDisabled = hasExistingRetention || retentionLimitReached;
            return (
            <div className="mt-2 pt-2 border-top">
              <div className="row align-items-center g-2">
                <div className="col-12 col-md-7">
                  <label className="form-label small mb-0 text-muted">
                    {translate('salary.retain.input.label', language)}
                  </label>
                </div>
                <div className="col-12 col-md-5">
                  <div className="input-group input-group-sm">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={retentionPct ?? ''}
                      inputMode="decimal"
                      disabled={isDisabled}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Permettre les nombres avec décimales
                        if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                          const numValue = value === '' ? 0 : parseFloat(value);
                          if (value === '' || (numValue >= 0 && numValue <= 100)) {
                            onRetentionChange(player.playerid, numValue);
                          }
                        }
                      }}
                      onBlur={(e) => {
                        // S'assurer qu'il y a une valeur valide au blur
                        const value = e.target.value;
                        if (value === '' || isNaN(parseFloat(value))) {
                          onRetentionChange(player.playerid, 0);
                        }
                      }}
                      placeholder="0"
                      title={hasExistingRetention ? (language === 'fr' ? 'Ce joueur a déjà une rétention active' : 'This player already has an active retention') : ''}
                    />
                    <span className="input-group-text">%</span>
                  </div>
                  {hasExistingRetention && (
                    <div className="form-text text-warning" style={{ fontSize: '0.7rem' }}>
                      ⚠️ {language === 'fr' ? 'Rétention existante: ' : 'Existing retention: '}{(player as any).salary_retention_received_pct}%
                    </div>
                  )}
                  {retentionLimitReached && (
                    <div className="form-text text-danger" style={{ fontSize: '0.7rem' }}>
                      ⚠️ {language === 'fr' ? `Limite atteinte (${retentionCount}/${maxRetentionSpots})` : `Limit reached (${retentionCount}/${maxRetentionSpots})`}
                    </div>
                  )}
                  {!hasExistingRetention && !retentionLimitReached && (
                    <div className="form-text" style={{ fontSize: '0.7rem' }}>
                      {translate('salary.retain.input.helper', language)}
                    </div>
                  )}
                </div>
              </div>
            </div>
            );
          })()}
        </div>
        
        <div className="d-flex align-items-center justify-content-center w-100 d-md-block w-md-auto">
          <button
            className={`btn ${isTradeOffer ? 'btn-outline-danger' : 'btn-outline-primary'} btn-sm`}
            onClick={() => onAction(player)}
            style={{ minWidth: '100px' }}
          >
            <span className="small">{actionLabel}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
);

// DraftPickCard component to display draft pick information
const DraftPickCard = ({ 
  draftPick, 
  onAction, 
  actionLabel, 
  language, 
  isTradeOffer = false,
  theme
}: {
  draftPick: DraftPick;
  onAction: (pick: DraftPick) => void;
  actionLabel: string;
  language: string;
  isTradeOffer?: boolean;
  theme: string;
}) => (
  <div className={`card mb-2 border-0 shadow-sm hover-lift ${isTradeOffer ? 'border-primary border-2' : ''}`} 
       style={{ transition: 'all 0.3s ease' }}>
    <div className="card-body p-2 p-md-3">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-2 gap-md-3">
        <div className="flex-grow-1 w-100">
          <div className="d-flex align-items-center gap-2 mb-2">
            <div className="d-flex align-items-center gap-2">
              <div>
                <h6 className={`mb-0 fw-bold small d-md-none ${theme === 'dark' ? 'text-white' : 'text-primary'}`}>{draftPick.description}</h6>
                <h6 className={`mb-0 fw-bold d-none d-md-block ${theme === 'dark' ? 'text-white' : 'text-primary'}`}>{draftPick.description}</h6>
                <div className="text-muted small">Choix de repêchage</div>
              </div>
            </div>
            <span className="badge rounded-pill bg-info">
              <span className="d-none d-md-inline">Choix de draft</span>
              <span className="d-md-none">Draft</span>
            </span>
          </div>
          
          <div className="row g-1 g-md-2 mb-2">
            <div className="col-12">
              <small className="text-muted d-block">
                <strong className="d-none d-md-inline">Type:</strong>
                <strong className="d-md-none">Type:</strong>
                <span> Choix de repêchage</span>
              </small>
            </div>
            {draftPick.originalUserName && (
              <div className="col-12">
                <small className="text-muted d-block">
                  <strong className="d-none d-md-inline">Propriétaire original:</strong>
                  <strong className="d-md-none">Orig:</strong>
                  <span className={`fw-semibold ${theme === 'dark' ? 'text-white' : 'text-info'}`}> {draftPick.originalUserName}</span>
                </small>
              </div>
            )}
          </div>
        </div>
        
        <div className="d-flex align-items-center justify-content-center w-100 d-md-block w-md-auto">
          <button
            className={`btn ${isTradeOffer ? 'btn-outline-danger' : 'btn-outline-primary'} btn-sm`}
            onClick={() => onAction(draftPick)}
            style={{ minWidth: '100px' }}
          >
            <span className="small">{actionLabel}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
);

// SalaryCapCard component to display salary cap information
const SalaryCapCard = ({ 
  salaryCapInfo, 
  teamName, 
  language, 
  theme 
}: {
  salaryCapInfo: any;
  teamName: string;
  language: string;
  theme: string;
}) => {
  if (!salaryCapInfo || !salaryCapInfo.isSalaryCapEnabled) {
    return (
      <div className={`card mb-3 border-0 shadow-sm`}>
        <div className="card-body p-3">
          <h6 className={`card-title mb-2 ${theme === 'dark' ? 'text-white' : 'text-dark'}`}>
            <strong>💰 Masse salariale - {teamName}</strong>
          </h6>
          <p className="text-muted small mb-0">Plafond salarial désactivé dans cette ligue</p>
        </div>
      </div>
    );
  }

  const { mainClub, reserves } = salaryCapInfo;
  
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      notation: 'standard'
    }).format(amount);
  };

  const getProgressBarColor = (percentage: number) => {
    if (percentage >= 95) return 'bg-danger';
    if (percentage >= 85) return 'bg-warning';
    if (percentage >= 70) return 'bg-info';
    return 'bg-success';
  };

  return (
    <div className={`card mb-3 border-0 shadow-sm`}>
      <div className="card-body p-3">
        <h6 className={`card-title mb-3 ${theme === 'dark' ? 'text-white' : 'text-dark'}`}>
          <strong>💰 Masse salariale - {teamName}</strong>
        </h6>
        
        {/* Club principal */}
        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className={`small fw-bold ${theme === 'dark' ? 'text-white' : 'text-dark'}`}>
              🏒 Club principal
            </span>
            <span className={`small ${theme === 'dark' ? 'text-white' : 'text-dark'}`}>
              {Math.round(mainClub.percentage)}%
            </span>
          </div>
          <div className="progress mb-2" style={{ height: '8px' }}>
            <div 
              className={`progress-bar ${getProgressBarColor(mainClub.percentage)}`}
              role="progressbar" 
              style={{ width: `${Math.min(mainClub.percentage, 100)}%` }}
            ></div>
          </div>
          <div className="row g-2 small">
            <div className="col-6">
              <div className="text-muted">Utilisé:</div>
              <div className={`fw-bold ${theme === 'dark' ? 'text-white' : 'text-dark'}`}>
                {formatCurrency(mainClub.current)}
              </div>
            </div>
            <div className="col-6">
              <div className="text-muted">Disponible:</div>
              <div className={`fw-bold ${mainClub.available >= 0 ? 'text-success' : 'text-danger'}`}>
                {formatCurrency(mainClub.available)}
              </div>
            </div>
          </div>
          <div className="text-muted small mt-1">
            Limite: {formatCurrency(mainClub.max)}
          </div>
        </div>

        {/* Réserves (si activées) */}
        {reserves.max > 0 && (
          <div>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className={`small fw-bold ${theme === 'dark' ? 'text-white' : 'text-dark'}`}>
                🥅 Réserves
              </span>
              <span className={`small ${theme === 'dark' ? 'text-white' : 'text-dark'}`}>
                {Math.round(reserves.percentage)}%
              </span>
            </div>
            <div className="progress mb-2" style={{ height: '8px' }}>
              <div 
                className={`progress-bar ${getProgressBarColor(reserves.percentage)}`}
                role="progressbar" 
                style={{ width: `${Math.min(reserves.percentage, 100)}%` }}
              ></div>
            </div>
            <div className="row g-2 small">
              <div className="col-6">
                <div className="text-muted">Utilisé:</div>
                <div className={`fw-bold ${theme === 'dark' ? 'text-white' : 'text-dark'}`}>
                  {formatCurrency(reserves.current)}
                </div>
              </div>
              <div className="col-6">
                <div className="text-muted">Disponible:</div>
                <div className={`fw-bold ${reserves.available >= 0 ? 'text-success' : 'text-danger'}`}>
                  {formatCurrency(reserves.available)}
                </div>
              </div>
            </div>
            <div className="text-muted small mt-1">
              Limite: {formatCurrency(reserves.max)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default function Trade() {
  // Tracker cette page
  usePageTracking('trade');

  const { isStandardFlexible, isLoading: modeLoading } = useLeagueMode();

  const navigate = useNavigate();
  const currentUserId = useUserStore((state) => state.user_id);
  const selectedLeague = useLeagueStore((state) => state.selectedLeague);
  const { language } = useLanguageStore();
  const { theme } = useThemeStore();

  const [selectedOpponentTeam, setSelectedOpponentTeam] = useState('');
  const [opponentTeams, setOpponentTeams] = useState<any[]>([]);
  const [myNhlTeamAbbrev, setMyNhlTeamAbbrev] = useState<string | null>(null);
  const [userPlayers, setUserPlayers] = useState<any[]>([]);
  const [userGoalies, setUserGoalies] = useState<any[]>([]);
  const [opponentPlayers, setOpponentPlayers] = useState<any[]>([]);
  const [opponentGoalies, setOpponentGoalies] = useState<any[]>([]);
  const [userTradeOffer, setUserTradeOffer] = useState<any[]>([]);
  const [opponentTradeOffer, setOpponentTradeOffer] = useState<any[]>([]);
  const [opponentTeamId, setOpponentTeamId] = useState<number | null>(null);
  
  // États pour les choix de draft
  const [userDraftPicks, setUserDraftPicks] = useState<any[]>([]);
  const [opponentDraftPicks, setOpponentDraftPicks] = useState<any[]>([]);
  const [userDraftPicksTradeOffer, setUserDraftPicksTradeOffer] = useState<any[]>([]);
  const [opponentDraftPicksTradeOffer, setOpponentDraftPicksTradeOffer] = useState<any[]>([]);

  // États pour la masse salariale
  const [userSalaryCapInfo, setUserSalaryCapInfo] = useState<any>(null);
  const [opponentSalaryCapInfo, setOpponentSalaryCapInfo] = useState<any>(null);

  // États pour la retenue de salaire
  const [userPlayerRetentions, setUserPlayerRetentions] = useState<{[key: string]: number}>({});
  const [opponentPlayerRetentions, setOpponentPlayerRetentions] = useState<{[key: string]: number}>({});
  const [isSalaryRetainEnabled, setIsSalaryRetainEnabled] = useState(false);
  const [salaryRetainMaxSpot, setSalaryRetainMaxSpot] = useState<number>(3);
  const [userRetentionCount, setUserRetentionCount] = useState<number>(0);
  const [opponentRetentionCount, setOpponentRetentionCount] = useState<number>(0);

  useEffect(() => {
    if (selectedLeague && currentUserId) {
      const fetchOpponentTeams = async () => {
        const { data, error } = await getUserByLeaguSaufUserConnecter(
          selectedLeague.id,
          parseInt(currentUserId)
        );

        if (error) {
          console.error('Error fetching opponent teams:', error);
        } else {
          setOpponentTeams(data || []);
        }

        // Fetch logo de l'utilisateur courant
        const { data: logoData } = await supabase
          .from('leagues_users')
          .select('nhl_team_abbrev')
          .eq('league_id', selectedLeague.id)
          .eq('user_id', parseInt(currentUserId))
          .single();

        if (logoData) {
          setMyNhlTeamAbbrev((logoData as { nhl_team_abbrev?: string | null }).nhl_team_abbrev ?? null);
        }
      };

      fetchOpponentTeams();
    }
  }, [selectedLeague, currentUserId]);

  useEffect(() => {
    if (selectedLeague && currentUserId) {
      const fetchUserData = async () => {
        try {
          // Fetch players
          const { data: playerData, error: playerError } = await getMyteamPlayer(
            selectedLeague.id,
            parseInt(currentUserId)
          );
          if (playerError) throw playerError;
          // Trier les joueurs par points (du plus élevé au plus bas)
          const sortedPlayerData = (playerData || []).sort((a: any, b: any) => (b.points || 0) - (a.points || 0));
          setUserPlayers(sortedPlayerData);

          // Fetch goalies
          const { data: goalieData, error: goalieError } = await getMyTeamGoalies(
            selectedLeague.id,
            parseInt(currentUserId)
          );
          if (goalieError) throw goalieError;
          // Trier les gardiens par nombre de victoires (du plus élevé au plus bas)
          const sortedGoalieData = (goalieData || []).sort((a: any, b: any) => (b.wins || 0) - (a.wins || 0));
          setUserGoalies(sortedGoalieData);

          // Fetch salary cap info
          const { data: salaryCapData, error: salaryCapError } = await getTeamSalaryCapInfo(
            selectedLeague.id,
            parseInt(currentUserId)
          );
          if (salaryCapError) {
            console.error('Error fetching salary cap info:', salaryCapError);
          } else {
            setUserSalaryCapInfo(salaryCapData);
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      };

      fetchUserData();
    }
  }, [selectedLeague, currentUserId]);

  useEffect(() => {
    const fetchOpponentData = async () => {
      if (selectedLeague && selectedOpponentTeam) {
        try {
          const { data: teamData, error: teamError } = await getTeamIdByTeamName(
            selectedLeague.id,
            selectedOpponentTeam
          );

          if (teamError) {
            console.error('Error fetching team ID:', teamError);
            return;
          }

          if (teamData && teamData.length > 0) {
            const teamId = teamData[0].users_user_id;
            setOpponentTeamId(teamId);

            // Fetch opponent players
            const { data: playerData, error: playerError } = await getMyteamPlayer(
              selectedLeague.id,
              teamId
            );
            if (playerError) throw playerError;
            // Trier les joueurs adverses par points (du plus élevé au plus bas)
            const sortedOpponentPlayerData = (playerData || []).sort((a: any, b: any) => (b.points || 0) - (a.points || 0));
            setOpponentPlayers(sortedOpponentPlayerData);

            // Fetch opponent goalies
            const { data: goalieData, error: goalieError } = await getMyTeamGoalies(
              selectedLeague.id,
              teamId
            );
            if (goalieError) throw goalieError;
            // Trier les gardiens adverses par nombre de victoires (du plus élevé au plus bas)
            const sortedOpponentGoalieData = (goalieData || []).sort((a: any, b: any) => (b.wins || 0) - (a.wins || 0));
            setOpponentGoalies(sortedOpponentGoalieData);

            // Fetch opponent salary cap info
            const { data: opponentSalaryCapData, error: opponentSalaryCapError } = await getTeamSalaryCapInfo(
              selectedLeague.id,
              teamId
            );
            if (opponentSalaryCapError) {
              console.error('Error fetching opponent salary cap info:', opponentSalaryCapError);
            } else {
              setOpponentSalaryCapInfo(opponentSalaryCapData);
            }
          }
        } catch (err) {
          console.error('Unexpected error:', err);
        }
      } else {
        setOpponentPlayers([]);
        setOpponentGoalies([]);
        setOpponentSalaryCapInfo(null);
      }
    };

    fetchOpponentData();
  }, [selectedLeague, selectedOpponentTeam]);

  // useEffect pour charger les choix de draft
  useEffect(() => {
    const fetchDraftPicks = async () => {
      if (selectedLeague && currentUserId) {
        try {
          // Charger les choix de draft de l'utilisateur
          const { data: userPicks, error: userPicksError } = await getUserDraftPicks(
            selectedLeague.id,
            parseInt(currentUserId)
          );
          if (userPicksError) throw userPicksError;
          setUserDraftPicks(userPicks || []);

          // Charger les choix de draft de l'adversaire si sélectionné
          if (opponentTeamId) {
            const { data: opponentPicks, error: opponentPicksError } = await getUserDraftPicks(
              selectedLeague.id,
              opponentTeamId
            );
            if (opponentPicksError) throw opponentPicksError;
            setOpponentDraftPicks(opponentPicks || []);
          } else {
            setOpponentDraftPicks([]);
          }
        } catch (error) {
          console.error('Error fetching draft picks:', error);
        }
      }
    };

    fetchDraftPicks();
  }, [selectedLeague, currentUserId, opponentTeamId]);

  // useEffect pour charger les compteurs de rétention
  useEffect(() => {
    const fetchRetentionCounts = async () => {
      if (!selectedLeague || !currentUserId) return;
      
      try {
        // Importer supabase dynamiquement si nécessaire
        const { supabase } = await import('../../lib/supabase');
        
        // Charger le compteur de l'utilisateur
        const { data: userData, error: userError } = await supabase
          .from('leagues_users')
          .select('number_of_salaryretain')
          .eq('league_id', selectedLeague.id)
          .eq('user_id', parseInt(currentUserId))
          .single();
        
        if (!userError && userData) {
          setUserRetentionCount(userData.number_of_salaryretain ?? 0);
        }
        
        // Charger le compteur de l'adversaire si sélectionné
        if (opponentTeamId) {
          const { data: opponentData, error: opponentError } = await supabase
            .from('leagues_users')
            .select('number_of_salaryretain')
            .eq('league_id', selectedLeague.id)
            .eq('user_id', opponentTeamId)
            .single();
          
          if (!opponentError && opponentData) {
            setOpponentRetentionCount(opponentData.number_of_salaryretain ?? 0);
          }
        } else {
          setOpponentRetentionCount(0);
        }
      } catch (error) {
        console.error('Error fetching retention counts:', error);
      }
    };

    fetchRetentionCounts();
  }, [selectedLeague, currentUserId, opponentTeamId]);

  const handleAddToTrade = (player: any, isUser: boolean) => {
    const playerName = player.skaterfullname || player.goaliefullname;
    
    if (isUser) {
      setUserTradeOffer([...userTradeOffer, player]);
      if (player.goaliefullname) {
        setUserGoalies(userGoalies.filter((p) => p.playerid !== player.playerid));
      } else {
        setUserPlayers(userPlayers.filter((p) => p.playerid !== player.playerid));
      }
      toast.success(`${playerName} ${translate('added.to.trade', language) || 'ajouté à l\'échange'}`, {
        duration: 2000,
        position: 'top-center'
      });
    } else {
      setOpponentTradeOffer([...opponentTradeOffer, player]);
      if (player.goaliefullname) {
        setOpponentGoalies(opponentGoalies.filter((p) => p.playerid !== player.playerid));
      } else {
        setOpponentPlayers(opponentPlayers.filter((p) => p.playerid !== player.playerid));
      }
      toast.success(`${playerName} ${translate('added.to.trade', language) || 'ajouté à l\'échange'}`, {
        duration: 2000,
        position: 'top-center'
      });
    }
    
    // Scroll automatique vers la section d'échange pour voir le joueur ajouté
    setTimeout(() => {
      const tradeSection = document.querySelector('.card.border-success');
      if (tradeSection) {
        tradeSection.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      } else {
        // Si la section d'échange n'existe pas encore, scroll vers le haut
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  const handleRemoveFromTrade = (player: any, isUser: boolean) => {
    if (isUser) {
      if (player.goaliefullname) {
        setUserGoalies([...userGoalies, player]);
      } else {
        setUserPlayers([...userPlayers, player]);
      }
      setUserTradeOffer(userTradeOffer.filter((p) => p.playerid !== player.playerid));
      // Supprimer la retenue de salaire lors du retrait du joueur
      const newRetentions = { ...userPlayerRetentions };
      delete newRetentions[player.playerid];
      setUserPlayerRetentions(newRetentions);
    } else {
      if (player.goaliefullname) {
        setOpponentGoalies([...opponentGoalies, player]);
      } else {
        setOpponentPlayers([...opponentPlayers, player]);
      }
      setOpponentTradeOffer(opponentTradeOffer.filter((p) => p.playerid !== player.playerid));
      // Supprimer la retenue de salaire lors du retrait du joueur
      const newRetentions = { ...opponentPlayerRetentions };
      delete newRetentions[player.playerid];
      setOpponentPlayerRetentions(newRetentions);
    }
  };

  // Fonction pour mettre à jour le pourcentage de retenue de salaire
  const handleUpdateRetention = (playerId: string, percentage: number, isUser: boolean) => {
    if (isUser) {
      setUserPlayerRetentions({ ...userPlayerRetentions, [playerId]: percentage });
    } else {
      setOpponentPlayerRetentions({ ...opponentPlayerRetentions, [playerId]: percentage });
    }
  };

  // Fonction pour ajouter un choix de draft à l'échange
  const handleAddDraftPickToTrade = (draftPick: any, isUser: boolean) => {
    if (isUser) {
      setUserDraftPicksTradeOffer([...userDraftPicksTradeOffer, draftPick]);
      setUserDraftPicks(userDraftPicks.filter((pick) => pick.id !== draftPick.id));
      toast.success(`${draftPick.description} ajouté à l'échange`, {
        duration: 2000,
        position: 'top-center'
      });
    } else {
      setOpponentDraftPicksTradeOffer([...opponentDraftPicksTradeOffer, draftPick]);
      setOpponentDraftPicks(opponentDraftPicks.filter((pick) => pick.id !== draftPick.id));
      toast.success(`${draftPick.description} ajouté à l'échange`, {
        duration: 2000,
        position: 'top-center'
      });
    }

    // Scroll automatique vers la section d'échange
    setTimeout(() => {
      const tradeSection = document.querySelector('.card.border-success');
      if (tradeSection) {
        tradeSection.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }, 100);
  };

  // Fonction pour retirer un choix de draft de l'échange
  const handleRemoveDraftPickFromTrade = (draftPick: any, isUser: boolean) => {
    if (isUser) {
      setUserDraftPicks([...userDraftPicks, draftPick]);
      setUserDraftPicksTradeOffer(userDraftPicksTradeOffer.filter((pick) => pick.id !== draftPick.id));
    } else {
      setOpponentDraftPicks([...opponentDraftPicks, draftPick]);
      setOpponentDraftPicksTradeOffer(opponentDraftPicksTradeOffer.filter((pick) => pick.id !== draftPick.id));
    }
  };

  const [tradeCooldown, setTradeCooldown] = useState(false);
  const [allowCounterOffers, setAllowCounterOffers] = useState(false);
  const [tradeDeadline, setTradeDeadline] = useState<Date | null>(null);
  const [isTradeDeadlinePassed, setIsTradeDeadlinePassed] = useState(false);
  const [showSalaryInfo, setShowSalaryInfo] = useState(false);
  // Placement modal state
  const [showPlacementModal, setShowPlacementModal] = useState(false);
  const [playersForPlacement, setPlayersForPlacement] = useState<PlayerPlacementItem[]>([]);

  useEffect(() => {
    let mounted = true;
    async function loadSetting() {
      if (!selectedLeague) return;
      try {
        const { data, error } = await getLeagueSettings(selectedLeague.id);
        if (error) {
          console.error('Error loading league settings:', error);
          return;
        }
        if (mounted) {
          setAllowCounterOffers(!!data?.conteroffer);
          setIsSalaryRetainEnabled(!!data?.salaryretain);
          setSalaryRetainMaxSpot(data?.salaryretainmaxspot ?? 3);
          
          // Charger la date limite des échanges
          if (data?.trade_deadline) {
            const deadline = new Date(data.trade_deadline);
            setTradeDeadline(deadline);
            
            // Vérifier si la date limite est dépassée
            const now = new Date();
            setIsTradeDeadlinePassed(now > deadline);
          }
        }
      } catch (e) {
        console.error('Failed to fetch league settings:', e);
      }
    }

    loadSetting();
    return () => { mounted = false; };
  }, [selectedLeague]);

    const handleSubmitTrade = async () => {
    if (!selectedLeague || !currentUserId || !opponentTeamId) {
      alert(translate('select.opponent.first', language));
      return;
    }

    // Validation: Vérifier le nombre de retentions utilisées
    const userNewRetentions = userTradeOffer.filter(player => {
      const retentionPct = userPlayerRetentions[player.playerid] || 0;
      return retentionPct > 0;
    }).length;
    
    const opponentNewRetentions = opponentTradeOffer.filter(player => {
      const retentionPct = opponentPlayerRetentions[player.playerid] || 0;
      return retentionPct > 0;
    }).length;

    if (userRetentionCount + userNewRetentions > salaryRetainMaxSpot) {
      alert(
        language === 'fr'
          ? `Vous ne pouvez pas soumettre cet échange. Limite de rétention atteinte (${userRetentionCount}/${salaryRetainMaxSpot}). Cet échange ajouterait ${userNewRetentions} rétention(s) supplémentaire(s).`
          : `You cannot submit this trade. Retention limit reached (${userRetentionCount}/${salaryRetainMaxSpot}). This trade would add ${userNewRetentions} additional retention(s).`
      );
      return;
    }

    if (opponentRetentionCount + opponentNewRetentions > salaryRetainMaxSpot) {
      alert(
        language === 'fr'
          ? `L'adversaire ne peut pas accepter cet échange. Limite de rétention atteinte (${opponentRetentionCount}/${salaryRetainMaxSpot}). Cet échange ajouterait ${opponentNewRetentions} rétention(s) supplémentaire(s).`
          : `Opponent cannot accept this trade. Retention limit reached (${opponentRetentionCount}/${salaryRetainMaxSpot}). This trade would add ${opponentNewRetentions} additional retention(s).`
      );
      return;
    }

    // Validation: Vérifier qu'on n'essaie pas de retenir du salaire sur un joueur qui a déjà une rétention active
    for (const player of userTradeOffer) {
      const playerId = player.playerid;
      const retentionPct = userPlayerRetentions[playerId] || 0;
      const hasExistingRetention = (player as any).salary_retention_received_pct && (player as any).salary_retention_received_pct > 0;
      
      if (retentionPct > 0 && hasExistingRetention) {
        const playerName = player.skaterfullname || player.goaliefullname || 'ce joueur';
        alert(
          language === 'fr' 
            ? `Impossible de retenir du salaire pour ${playerName}: ce joueur a déjà une rétention active de ${(player as any).salary_retention_received_pct}%`
            : `Cannot retain salary for ${playerName}: this player already has an active retention of ${(player as any).salary_retention_received_pct}%`
        );
        return;
      }
    }

    for (const player of opponentTradeOffer) {
      const playerId = player.playerid;
      const retentionPct = opponentPlayerRetentions[playerId] || 0;
      const hasExistingRetention = (player as any).salary_retention_received_pct && (player as any).salary_retention_received_pct > 0;
      
      if (retentionPct > 0 && hasExistingRetention) {
        const playerName = player.skaterfullname || player.goaliefullname || 'ce joueur';
        alert(
          language === 'fr'
            ? `Impossible de retenir du salaire pour ${playerName}: ce joueur a déjà une rétention active de ${(player as any).salary_retention_received_pct}%`
            : `Cannot retain salary for ${playerName}: this player already has an active retention of ${(player as any).salary_retention_received_pct}%`
        );
        return;
      }
    }

    // Si des joueurs adverses seront reçus, afficher le modal de placement d'abord
    const playersToPlace: PlayerPlacementItem[] = opponentTradeOffer.map(p => ({
      id: p.playerid,
      name: (p.skaterfullname || p.goaliefullname || '') as string,
      position: p.positioncode as string | undefined,
      salary: p.salary as number | undefined,
    }));

    if (playersToPlace.length > 0) {
      setPlayersForPlacement(playersToPlace);
      setShowPlacementModal(true);
      return; // On attend la confirmation du modal
    }

    // Aucun joueur à placer — soumettre directement
    await doSubmitTrade({});
  };

  // Appelé quand l'utilisateur confirme le placement des joueurs reçus
  const handlePlacementModalConfirm = (placements: Record<string, 'main' | 'reserve'>) => {
    setShowPlacementModal(false);
    setPlayersForPlacement([]);
    doSubmitTrade(placements);
  };

  const handlePlacementModalCancel = () => {
    setShowPlacementModal(false);
    setPlayersForPlacement([]);
  };

  // Logique principale de soumission de l'échange
  const doSubmitTrade = async (placements: Record<string, 'main' | 'reserve'>) => {
    if (!selectedLeague || !currentUserId || !opponentTeamId) return;

    setTradeCooldown(true);
    setTimeout(() => setTradeCooldown(false), 5000);

    try {
      let firstTradeId: number | undefined;

      // Combiner les joueurs et les draft picks pour créer une liste unifiée
      const userAssets = [
        ...userTradeOffer.map(player => ({ type: 'player', data: player })),
        ...userDraftPicksTradeOffer.map(pick => ({ type: 'pick', data: pick }))
      ];
      
      const opponentAssets = [
        ...opponentTradeOffer.map(player => ({ type: 'player', data: player })),
        ...opponentDraftPicksTradeOffer.map(pick => ({ type: 'pick', data: pick }))
      ];

      const maxLength = Math.max(userAssets.length, opponentAssets.length);

      for (let i = 0; i < maxLength; i++) {
        const userAsset = userAssets[i];
        const opponentAsset = opponentAssets[i];

        // Extraire les IDs selon le type d'asset
        const userPlayerId = userAsset?.type === 'player' ? userAsset.data.playerid : '';
        const userPickId = userAsset?.type === 'pick' ? userAsset.data.id : null;
        
        const opponentPlayerId = opponentAsset?.type === 'player' ? opponentAsset.data.playerid : '';
        const opponentPickId = opponentAsset?.type === 'pick' ? opponentAsset.data.id : null;

        // Extraire les pourcentages de retenue de salaire (0 pour les draft picks)
        const userRetentionPct = userAsset?.type === 'player' ? (userPlayerRetentions[userPlayerId] || 0) : 0;
        const opponentRetentionPct = opponentAsset?.type === 'player' ? (opponentPlayerRetentions[opponentPlayerId] || 0) : 0;

        const { data, error } = await proposeTradeWithNotification(
          selectedLeague.id,
          i,
          Number(currentUserId),
          userPlayerId,
          userPickId,
          opponentTeamId,
          opponentPlayerId,
          opponentPickId,
          false,
          false,
          firstTradeId,
          userRetentionPct,
          opponentRetentionPct
        );

        if (error) {
          console.error('Error submitting trade:', error);
          throw error;
        }
        
        // Stocker le th_id de la première ligne pour les lignes suivantes
        if (i === 0 && data) {
          firstTradeId = data;
          // Mettre à jour le trades_id pour la première ligne
          if (firstTradeId) {
            await updateTradesId(firstTradeId);
          }
        }
      }

      // Sauvegarder les préférences de placement du proposant (joueurs qu'il recevra)
      if (Object.keys(placements).length > 0 && firstTradeId) {
        try {
          await saveTradePreferences(
            firstTradeId,
            selectedLeague.id,
            Number(currentUserId),
            placements
          );
        } catch (prefError) {
          console.warn('Erreur sauvegarde préférences de placement:', prefError);
          // Non-bloquant
        }
      }

      // Créer une enchère si l'option est cochée
      if (allowCounterOffers && firstTradeId) {
        try {
          const senderAssets = {
            players: userTradeOffer.filter(p => !p.goaliefullname).map(p => p.playerid),
            goalies: userTradeOffer.filter(p => p.goaliefullname).map(p => p.playerid),
            picks: userDraftPicksTradeOffer.map(p => p.id)
          };

          const receiverAssets = {
            players: opponentTradeOffer.filter(p => !p.goaliefullname).map(p => p.playerid),
            goalies: opponentTradeOffer.filter(p => p.goaliefullname).map(p => p.playerid),
            picks: opponentDraftPicksTradeOffer.map(p => p.id)
          };

          const auctionResult = await createTradeAuction(
            selectedLeague.id,
            firstTradeId,
            Number(currentUserId),
            opponentTeamId,
            senderAssets,
            receiverAssets
          );

          console.log('🔥 Paramètres enchère:', {
            leagueId: selectedLeague.id,
            tradeId: firstTradeId,
            initiatorId: Number(currentUserId),
            targetId: opponentTeamId,
            senderAssets,
            receiverAssets
          });
          console.log('🔥 Résultat enchère:', auctionResult);

          if (auctionResult.data?.success) {
            toast.success('Enchère créée! Les autres DG peuvent maintenant faire des contre-offres pendant 24h.');
          } else {
            console.warn('❌ Erreur création enchère:', auctionResult.error);
            toast.error('Erreur lors de la création de l\'enchère: ' + (auctionResult.error?.message || 'Erreur inconnue'));
          }
        } catch (auctionError) {
          console.warn('Erreur création enchère:', auctionError);
          // Non-bloquant
        }
      }

      // Envoyer l'email de notification complet après avoir soumis tous les trades
      if (firstTradeId) {
        try {
          await sendCompleteTradeNotification(
            selectedLeague.id,
            firstTradeId,
            opponentTeamId
          );
        } catch (emailError) {
          console.warn('Error sending complete trade notification:', emailError);
        }
      }

      alert(translate('trade.submitted', language));
      setUserTradeOffer([]);
      setOpponentTradeOffer([]);
      setUserDraftPicksTradeOffer([]);
      setOpponentDraftPicksTradeOffer([]);
    } catch (err) {
      console.error('Unexpected error:', err);
      alert(translate('trade.error', language));
    }
  };

  // Mode guard
  if (!modeLoading && isStandardFlexible) {
    return (
      <ModeAccessMessage
        variant="dynasty-only"
        title="Échanges — Mode Dynasty / Keeper uniquement"
        message="Les échanges ne sont pas disponibles en mode Standard Flexible. Cette fonctionnalité est exclusive au mode Dynasty/Keeper."
      />
    );
  }

  return (
    <>
    <div id="trade-page" className="container-fluid px-3 px-md-4">
      <div className="row justify-content-center">
        <div className="col-12 col-xl-11">
          {/* Bouton retour - toujours visible */}
          <button
            className={`btn btn-link p-0 d-flex align-items-center mb-3 ${theme === 'dark' ? 'text-white' : 'text-primary'}`}
            onClick={() => navigate('/my-office')}
          >
            <ArrowLeft size={20} className="me-2" />
            <span>{translate('back', language)}</span>
          </button>
          
          {/* Vérifier si la date limite est dépassée */}
          {isTradeDeadlinePassed && tradeDeadline && (
            <div className="alert alert-danger d-flex align-items-center mb-4" role="alert">
              <AlertCircle size={24} className="me-3" />
              <div>
                <h5 className="alert-heading mb-2">
                  {language === 'fr' ? 'Date limite des échanges dépassée' : 'Trade Deadline Passed'}
                </h5>
                <p className="mb-0">
                  {language === 'fr' 
                    ? `La date limite pour effectuer des échanges était le ${tradeDeadline.toLocaleDateString('fr-CA')}. Il n'est plus possible de faire des échanges pour cette saison.`
                    : `The trade deadline was ${tradeDeadline.toLocaleDateString('en-US')}. Trades are no longer allowed for this season.`
                  }
                </p>
              </div>
            </div>
          )}
          
          {!isTradeDeadlinePassed && (
          <div className="card shadow-lg border-0 glass-effect">
            <div className="card-body p-3 p-md-5">
              {/* Header */}
              <div className="d-flex align-items-center justify-content-between mb-4">
                <button
                  className={`btn btn-link p-0 d-flex align-items-center ${theme === 'dark' ? 'text-white' : 'text-primary'}`}
                  onClick={() => goBackOrHome(navigate)}
                >
                  <ArrowLeft size={20} className="me-2" />
                  <span className="d-none d-sm-inline">{translate('back', language)}</span>
                </button>
                
                <div className="text-center flex-grow-1">
                  <div className="d-flex align-items-center justify-content-center gap-3 mb-2">
                    <Users className={theme === 'dark' ? 'text-white' : 'text-primary'} size={32} />
                    <ArrowRightLeft className="text-muted" size={24} />
                    <Users className="text-secondary" size={32} />
                  </div>
                  <h2 className="h4 mb-1 text-gradient-primary">{translate('trade.players', language)}</h2>
                  <p className="text-muted small mb-0">{translate('propose.trade', language)}</p>
                </div>
                
                <div style={{ width: '100px' }}></div> {/* Spacer for centering */}
              </div>

              {/* Team Selection */}
              <div className="row g-3 mb-4">
                <div className="col-md-6">
                  <div className="card border-primary border-2 h-100">
                    <div className="card-header bg-primary text-white text-center py-2">
                      {myNhlTeamAbbrev && (
                        <div className="d-flex justify-content-center mb-1">
                          <TeamLogo abbrev={myNhlTeamAbbrev} size={40} />
                        </div>
                      )}
                      <h6 className="mb-0 fw-bold">{translate('your.team', language)}</h6>
                    </div>
                    <div className="card-body p-3">
                      <select className="form-select form-select-lg" disabled>
                        <option value="MyTeam">{translate('your.team', language)}</option>
                      </select>
                    </div>
                  </div>
                </div>
                
                <div className="col-md-6">
                  <div className="card border-secondary border-2 h-100">
                    <div className="card-header bg-secondary text-white text-center py-2">
                      {(() => {
                        const opponentObj = opponentTeams.find((t: any) => t.username === selectedOpponentTeam);
                        return opponentObj?.nhl_team_abbrev ? (
                          <div className="d-flex justify-content-center mb-1">
                            <TeamLogo abbrev={opponentObj.nhl_team_abbrev} size={40} />
                          </div>
                        ) : null;
                      })()}
                      <h6 className="mb-0 fw-bold">{translate('opponent.team', language)}</h6>
                    </div>
                    <div className="card-body p-3">
                      <select
                        className="form-select form-select-lg"
                        value={selectedOpponentTeam}
                        onChange={(e) => setSelectedOpponentTeam(e.target.value)}
                      >
                        <option value="">{translate('select.opponent', language)}</option>
                        {opponentTeams.map((team) => (
                          <option key={team.user_id} value={team.username}>
                            {team.username}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bouton pour afficher/masquer les infos salariales */}
              {(userSalaryCapInfo || opponentSalaryCapInfo) && (
                <div className="text-center mb-3">
                  <button
                    className={`btn btn-sm ${showSalaryInfo ? 'btn-outline-secondary' : 'btn-outline-secondary'} ${theme === 'dark' ? 'text-white' : ''}`}
                    onClick={() => setShowSalaryInfo(!showSalaryInfo)}
                  >
                    {showSalaryInfo 
                      ? (language === 'fr' ? 'Masquer les informations salariales des équipes' : 'Hide Salary Information')
                      : (language === 'fr' ? 'Afficher les informations salariales des équipes' : 'Show Salary Information')
                    }
                  </button>
                </div>
              )}

              {/* Salary Cap Information */}
              {showSalaryInfo && (userSalaryCapInfo || opponentSalaryCapInfo) && (
                <div className="row g-3 mb-4">
                  <div className="col-md-6">
                    {userSalaryCapInfo && (
                      <SalaryCapCard
                        salaryCapInfo={userSalaryCapInfo}
                        teamName="Votre équipe"
                        language={language}
                        theme={theme}
                      />
                    )}
                  </div>
                  <div className="col-md-6">
                    {opponentSalaryCapInfo && selectedOpponentTeam && (
                      <SalaryCapCard
                        salaryCapInfo={opponentSalaryCapInfo}
                        teamName={selectedOpponentTeam}
                        language={language}
                        theme={theme}
                      />
                    )}
                  </div>
                </div>
              )}

              {/* Trade Offers Section */}
              {(userTradeOffer.length > 0 || opponentTradeOffer.length > 0 || userDraftPicksTradeOffer.length > 0 || opponentDraftPicksTradeOffer.length > 0) && (
                <div className="row g-3 mb-4">
                  <div className="col-12">
                    <div className="card border-success border-2 bg-success bg-opacity-10">
                      <div className="card-header bg-success text-white text-center">
                        <h5 className="mb-0 d-flex align-items-center justify-content-center gap-2">
                          <ArrowRightLeft size={20} />
                          {translate('trade.in.progress', language) || 'Échange en cours'}
                        </h5>
                      </div>
                      <div className="card-body">
                        <div className="row g-3">
                          <div className="col-md-6">
                            <h6 className={`fw-bold mb-3 ${theme === 'dark' ? 'text-white' : 'text-primary'}`}>{translate('your.trade.offer', language)}</h6>
                            {userTradeOffer.length === 0 && userDraftPicksTradeOffer.length === 0 ? (
                              <div className="text-center py-4 text-muted">
                                <p className="mb-0">{translate('no.players.selected', language) || 'Aucun joueur ou choix sélectionné'}</p>
                              </div>
                            ) : (
                              <div className="d-flex flex-column gap-2">
                                {/* Joueurs */}
                                {userTradeOffer.map((player) => (
                                  <PlayerCard
                                    key={player.playerid}
                                    player={player}
                                    onAction={(p: any) => handleRemoveFromTrade(p, true)}
                                    actionLabel={translate('remove', language)}
                                    language={language}
                                    isTradeOffer={true}
                                    theme={theme}
                                    isSalaryRetainEnabled={isSalaryRetainEnabled}
                                    retentionPct={userPlayerRetentions[player.playerid] || 0}
                                    onRetentionChange={(playerId: string, pct: number) => handleUpdateRetention(playerId, pct, true)}
                                    retentionCount={userRetentionCount}
                                    maxRetentionSpots={salaryRetainMaxSpot}
                                    isUserPlayer={true}
                                  />
                                ))}
                                
                                {/* Choix de draft */}
                                {userDraftPicksTradeOffer.map((draftPick) => (
                                  <DraftPickCard
                                    key={draftPick.id}
                                    draftPick={draftPick}
                                    onAction={(pick: any) => handleRemoveDraftPickFromTrade(pick, true)}
                                    actionLabel={translate('remove', language)}
                                    language={language}
                                    isTradeOffer={true}
                                    theme={theme}
                                  />
                                ))}
                              </div>
                            )}
                          </div>
                          
                          <div className="col-md-6">
                            <h6 className="text-secondary fw-bold mb-3">{translate('opponent.trade.offer', language)}</h6>
                            {opponentTradeOffer.length === 0 && opponentDraftPicksTradeOffer.length === 0 ? (
                              <div className="text-center py-4 text-muted">
                                <p className="mb-0">{translate('no.players.selected', language) || 'Aucun joueur ou choix sélectionné'}</p>
                              </div>
                            ) : (
                              <div className="d-flex flex-column gap-2">
                                {/* Joueurs */}
                                {opponentTradeOffer.map((player) => (
                                  <PlayerCard
                                    key={player.playerid}
                                    player={player}
                                    onAction={(p: any) => handleRemoveFromTrade(p, false)}
                                    actionLabel={translate('remove', language)}
                                    language={language}
                                    isTradeOffer={true}
                                    theme={theme}
                                    isSalaryRetainEnabled={isSalaryRetainEnabled}
                                    retentionPct={opponentPlayerRetentions[player.playerid] || 0}
                                    onRetentionChange={(playerId: string, pct: number) => handleUpdateRetention(playerId, pct, false)}
                                    retentionCount={opponentRetentionCount}
                                    maxRetentionSpots={salaryRetainMaxSpot}
                                    isUserPlayer={false}
                                  />
                                ))}
                                
                                {/* Choix de draft */}
                                {opponentDraftPicksTradeOffer.map((draftPick) => (
                                  <DraftPickCard
                                    key={draftPick.id}
                                    draftPick={draftPick}
                                    onAction={(pick: any) => handleRemoveDraftPickFromTrade(pick, false)}
                                    actionLabel={translate('remove', language)}
                                    language={language}
                                    isTradeOffer={true}
                                    theme={theme}
                                  />
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Option pour permettre les contre-offres */}
                        <div className="d-flex flex-column align-items-center mt-3 mb-3">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="allowCounterOffers"
                              checked={allowCounterOffers}
                              disabled={true}
                            />
                            <label className="form-check-label" htmlFor="allowCounterOffers">
                              🔥 Permettre les contre-offres (24h)
                            </label>
                          </div>
                          <div className="w-100 d-flex justify-content-center mt-2">
                            <small className="text-muted">Cette option est activable ou désactivable uniquement par les commissaires depuis les paramètres de la ligue.</small>
                          </div>
                        </div>
                        {allowCounterOffers && (
                          <div className="text-center mb-3">
                            <small className="text-muted">
                              Les autres DG pourront faire des contre-offres pendant 24h. 
                              Vous pourrez accepter la meilleure offre.
                            </small>
                          </div>
                        )}
                        
                        <div className="text-center mt-4">
                          <button
                            className="btn btn-success btn-lg px-5"
                            onClick={handleSubmitTrade}
                            disabled={(userTradeOffer.length === 0 && userDraftPicksTradeOffer.length === 0 && opponentTradeOffer.length === 0 && opponentDraftPicksTradeOffer.length === 0) || tradeCooldown}
                          >
                            {tradeCooldown ? (
                              <span className="d-flex align-items-center gap-2">
                                <div className="spinner-border spinner-border-sm" role="status"></div>
                                {translate('submit.trade', language)} (5s)
                              </span>
                            ) : (
                              <span className="d-flex align-items-center gap-2">
                                <ArrowRightLeft size={20} />
                                {translate('submit.trade', language)}
                              </span>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Players Section */}
              <div className="row g-2 g-md-3">
                <div className="col-12 col-md-6">
                  <div className="card h-100 border-primary">
                    <div className="card-header bg-primary text-white py-2">
                      <h6 className="mb-0 d-none d-md-block">{translate('your.team', language)}</h6>
                      <h6 className="mb-0 d-md-none small">{translate('your.team', language)}</h6>
                    </div>
                    <div className="card-body p-2 p-md-3">
                      {/* Your Players */}
                      <div className="mb-3 mb-md-4">
                        <h6 className={`fw-bold mb-2 mb-md-3 d-flex align-items-center gap-1 gap-md-2 ${theme === 'dark' ? 'text-white' : 'text-primary'}`}>
                          <Users size={16} className="d-md-none" />
                          <Users size={18} className="d-none d-md-block" />
                          <span className="d-none d-md-inline">{translate('your.players', language)} ({userPlayers.length})</span>
                          <span className="d-md-none small">Joueurs ({userPlayers.length})</span>
                        </h6>
                        <TradePlayersTable
                          data={userPlayers}
                          onAddToTrade={(p) => handleAddToTrade(p, true)}
                          language={language}
                          theme={theme}
                          isUserTeam={true}
                        />
                      </div>
                      
                      {/* Your Goalies */}
                      <div className="mb-3 mb-md-4">
                        <h6 className={`fw-bold mb-2 mb-md-3 d-flex align-items-center gap-1 gap-md-2 ${theme === 'dark' ? 'text-white' : 'text-primary'}`}>
                          <Star size={16} className="d-md-none" />
                          <Star size={18} className="d-none d-md-block" />
                          <span className="d-none d-md-inline">{translate('your.goalies', language)} ({userGoalies.length})</span>
                          <span className="d-md-none small">Gardiens ({userGoalies.length})</span>
                        </h6>
                        <TradeGoaliesTable
                          data={userGoalies}
                          onAddToTrade={(g) => handleAddToTrade(g, true)}
                          language={language}
                          theme={theme}
                          isUserTeam={true}
                        />
                      </div>

                      {/* Your Draft Picks */}
                      <div>
                        <h6 className={`fw-bold mb-2 mb-md-3 d-flex align-items-center gap-1 gap-md-2 ${theme === 'dark' ? 'text-white' : 'text-primary'}`}>
                          <Calendar size={16} className="d-md-none" />
                          <Calendar size={18} className="d-none d-md-block" />
                          <span className="d-none d-md-inline">Vos choix de draft ({userDraftPicks.length})</span>
                          <span className="d-md-none small">Choix ({userDraftPicks.length})</span>
                        </h6>
                        <TradeDraftPicksTable
                          data={userDraftPicks}
                          onAddToTrade={(pick) => handleAddDraftPickToTrade(pick, true)}
                          language={language}
                          theme={theme}
                          isUserTeam={true}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="col-12 col-md-6">
                  <div className="card h-100 border-secondary">
                    <div className="card-header bg-secondary text-white py-2">
                      <h6 className="mb-0 d-none d-md-block">{translate('opponent.team', language)}</h6>
                      <h6 className="mb-0 d-md-none small">{translate('opponent.team', language)}</h6>
                    </div>
                    <div className="card-body p-2 p-md-3">
                      {!selectedOpponentTeam ? (
                        <div className="text-center py-5">
                          <Users size={48} className="text-muted mb-3" />
                          <p className="text-muted">{translate('select.opponent.first', language)}</p>
                        </div>
                      ) : (
                        <>
                          {/* Opponent Players */}
                          <div className="mb-3 mb-md-4">
                            <h6 className="text-secondary fw-bold mb-2 mb-md-3 d-flex align-items-center gap-1 gap-md-2">
                              <Users size={16} className="d-md-none" />
                              <Users size={18} className="d-none d-md-block" />
                              <span className="d-none d-md-inline">{translate('opponent.players', language)} ({opponentPlayers.length})</span>
                              <span className="d-md-none small">Joueurs ({opponentPlayers.length})</span>
                            </h6>
                            <TradePlayersTable
                              data={opponentPlayers}
                              onAddToTrade={(p) => handleAddToTrade(p, false)}
                              language={language}
                              theme={theme}
                              isUserTeam={false}
                            />
                          </div>
                          
                          {/* Opponent Goalies */}
                          <div className="mb-3 mb-md-4">
                            <h6 className="text-secondary fw-bold mb-2 mb-md-3 d-flex align-items-center gap-1 gap-md-2">
                              <Star size={16} className="d-md-none" />
                              <Star size={18} className="d-none d-md-block" />
                              <span className="d-none d-md-inline">{translate('opponent.goalies', language)} ({opponentGoalies.length})</span>
                              <span className="d-md-none small">Gardiens ({opponentGoalies.length})</span>
                            </h6>
                            <TradeGoaliesTable
                              data={opponentGoalies}
                              onAddToTrade={(g) => handleAddToTrade(g, false)}
                              language={language}
                              theme={theme}
                              isUserTeam={false}
                            />
                          </div>

                          {/* Opponent Draft Picks */}
                          <div>
                            <h6 className="text-secondary fw-bold mb-2 mb-md-3 d-flex align-items-center gap-1 gap-md-2">
                              <Calendar size={16} className="d-md-none" />
                              <Calendar size={18} className="d-none d-md-block" />
                              <span className="d-none d-md-inline">Choix de draft adversaire ({opponentDraftPicks.length})</span>
                              <span className="d-md-none small">Choix ({opponentDraftPicks.length})</span>
                            </h6>
                            <TradeDraftPicksTable
                              data={opponentDraftPicks}
                              onAddToTrade={(pick) => handleAddDraftPickToTrade(pick, false)}
                              language={language}
                              theme={theme}
                              isUserTeam={false}
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          )}
        </div>
      </div>
    </div>

    {/* Modal de placement des joueurs reçus — affiché au proposant avant soumission */}
    <TradePlacementModal
      show={showPlacementModal}
      players={playersForPlacement}
      language={language}
      theme={theme}
      isProposer={true}
      onConfirm={handlePlacementModalConfirm}
      onCancel={handlePlacementModalCancel}
    />
    </>
  );
}

export { Trade };